/**
 * Custom error classes for location suggestions API operations
 * These provide consistent error messages for Sentry grouping
 * while preserving context data for debugging
 */

import { BaseApiFetchError } from '../../../../../utility/error/BaseApiFetchError'
import { BaseApiValidationError } from '../../../../../utility/error/BaseApiValidationError'

export class LocationSuggestionsFetchError extends BaseApiFetchError {
  public readonly query: string
  public readonly suggestionType: string

  constructor(
    url: string,
    query: string,
    suggestionType: 'campsite' | 'hierarchy' | 'recent',
    originalError: any,
  ) {
    // Create params object from search-specific parameters
    const params = { query, suggestionType }
    super('location-suggestions', url, params, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'LocationSuggestionsFetchError',
      enumerable: true,
    })
    this.query = query
    this.suggestionType = suggestionType
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   * Extends base context with search-specific fields for backwards compatibility
   */
  override getContext() {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      // Add search-specific context fields for debugging
      query: this.query,
      queryLength: this.query?.length,
      suggestionType: this.suggestionType,
    }
  }
}

export class LocationSuggestionsValidationError extends BaseApiValidationError {
  public readonly query: string
  public readonly suggestionType: string

  constructor(
    url: string,
    query: string,
    suggestionType: 'campsite' | 'hierarchy' | 'recent',
    rawResponse: any,
    originalError: any,
  ) {
    // Create params object from search-specific parameters
    const params = { query, suggestionType }
    super('location-suggestions', url, params, rawResponse, originalError)

    // Override name for backwards compatibility using Object.defineProperty like CustomError
    Object.defineProperty(this, 'name', {
      value: 'LocationSuggestionsValidationError',
      enumerable: true,
    })
    this.query = query
    this.suggestionType = suggestionType
  }

  /**
   * Get context data for Sentry or logging
   * Extends base context with search-specific fields for backwards compatibility
   */
  getContext() {
    const baseContext = super.getContext()
    return {
      ...baseContext,
      // Add search-specific context fields for debugging
      query: this.query,
      queryLength: this.query?.length,
      suggestionType: this.suggestionType,
    }
  }
}
